/**
 * QP-195 — per-goal platform state for the goal_input widget (advisor + public).
 *
 * The server owns all eligibility logic (goalType × derived statuses); this
 * hook just fetches `{ hasPlatforms, eligible, resolved, selectedId }` and
 * exposes a mutation for the explicit selection endpoint. Both surfaces share
 * the same query key so cascade responses can invalidate uniformly.
 */

import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as goalsApi from "@/lib/api/flow-instances/goals";
import * as publicGoalsApi from "@/lib/api/public-flow/goals";
import { usePublicSession } from "@/lib/context/public-session-context";
import { goalSnapshotQueryKeys } from "@/lib/hooks/use-flow-instances/use-goal-snapshots";
import { goalQueryKeys } from "@/lib/hooks/use-flow-instances/use-goals";
import { invalidatePortfolioFamily } from "@/lib/hooks/use-flow-instances/use-portfolios";
import { publicGoalQueryKeys } from "@/lib/hooks/use-public-flow/use-goals";
import { invalidatePublicPortfolioFamily } from "@/lib/hooks/use-public-flow/use-portfolios";

export const goalPlatformQueryKeys = {
  /**
   * Prefix-invalidating `state(goalId)` also drops every candidate-type
   * evaluation for that goal (`state(goalId, goalType)`).
   */
  state: (goalId: string | null, goalType?: string) =>
    goalType
      ? (["goalPlatforms", goalId, { goalType }] as const)
      : (["goalPlatforms", goalId] as const),
};

/**
 * QL-546 — submit re-syncs every goal's platform before its completion gate,
 * and that write stays when the gate refuses (422 / 409). Refetch what the
 * platform cascade may have changed: platform state, the goal list, each
 * goal's portfolios, snapshots and mandate, and every portfolio detail.
 * Otherwise the goal tab keeps the old platform and products already removed.
 */
export function invalidateGoalPlatformsAfterRefusedSubmit(
  queryClient: QueryClient,
  opts: { isPublic: boolean; flowInstanceId: string | null },
) {
  queryClient.invalidateQueries({ queryKey: ["goalPlatforms"] });
  if (opts.isPublic) {
    // Every per-goal read lives under ["publicFlow", "goals"].
    queryClient.invalidateQueries({ queryKey: publicGoalQueryKeys.goals() });
    invalidatePublicPortfolioFamily(queryClient);
  } else {
    queryClient.invalidateQueries({ queryKey: goalQueryKeys.goals(opts.flowInstanceId) });
    // Every per-goal read (portfolios, snapshots, mandate) lives under this prefix.
    queryClient.invalidateQueries({ queryKey: ["flowInstances", "goals"] });
    invalidatePortfolioFamily(queryClient);
  }
}

export function useGoalPlatformState(
  goalId: string | null,
  /** QL-281 — evaluate for a candidate (unsaved) goal type instead of the stored one. */
  goalType?: string,
) {
  const session = usePublicSession();
  const { data, error, isLoading } = useQuery({
    queryKey: goalPlatformQueryKeys.state(goalId, goalType),
    queryFn: () =>
      session.isPublic
        ? publicGoalsApi.getPublicGoalPlatforms(goalId!, session.sessionToken, goalType)
        : goalsApi.getGoalPlatforms(goalId!, goalType),
    enabled: !!goalId,
  });
  return { platformState: data ?? null, error, isLoading };
}

export function useSetGoalPlatform(goalId: string) {
  const session = usePublicSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ platformId, lang }: { platformId: string; lang?: string }) =>
      session.isPublic
        ? publicGoalsApi.setPublicGoalPlatform(goalId, platformId, session.sessionToken, lang)
        : goalsApi.setGoalPlatform(goalId, platformId, lang),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: goalPlatformQueryKeys.state(goalId) });
      // QL-375 — the goal's mandate resolves from its platform.
      queryClient.invalidateQueries({ queryKey: goalSnapshotQueryKeys.mandate(goalId) });
    },
  });
}
